import React, { ReactNode } from 'react';
import { motion } from 'framer-motion';
import Title from '../Title/Title';

interface TitleWithDescriptionProps {
    title: {
        firstLine?: ReactNode | string;
        secondLine?: ReactNode | string;
    };
    description: ReactNode | (() => ReactNode);
    layout?: 'row' | 'column';
    image?: {
        src: string;
        alt: string;
        position?: 'left' | 'right';
        className?: string;
    };
    link?: {
        text: string;
        onClick: () => void;
    };
    className?: string;
}

function TitleWithDescription({
    title,
    description,
    layout = 'row',
    image,
    link,
    className = ''
}: TitleWithDescriptionProps) {
    // if (!title.firstLine && !title.secondLine) {
    //     console.warn('TitleWithDescription: only description is provided...');
    // }
    const titleContent = (
        <Title
            firstLine={title.firstLine}
            secondLine={title.secondLine}
            className={layout === 'column' ? 'mb-6' : ''}
        />
    );

    const formatDescription = (text: string | ReactNode) => {
        if (typeof text !== 'string') return text;
        return text.split('\n').map((line, i, arr) => (
            <div key={i} className="mb-2">{line}</div>
        ));
    };

    const descriptionContent = (
        <motion.div
            className="space-y-4"
            initial={{ opacity: 0, x: layout === 'column' ? 0 : 20, y: layout === 'column' ? 20 : 0 }}
            whileInView={{ opacity: 1, x: 0, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
        >
            <div className="text-gray-600 text-extralight leading-[1.3] tracking-[0.03em]">
                {typeof description === 'function' ? description() : formatDescription(description)}
            </div>
            {link && (
                <button
                    onClick={link.onClick}
                    className="text-sm text-gray-900 hover:text-gray-600 transition-colors underline"
                >
                    {link.text}
                </button>
            )}
        </motion.div>
    );

    const contentSection = layout === 'column' ? (
        <div className="space-y-9">
            {titleContent}
            {descriptionContent}
        </div>
    ) : (
        <div className="grid md:grid-cols-2 gap-16">
            {titleContent}
            {descriptionContent}
        </div>
    );

    if (!image) {
        return (
            <div className={`w-full mx-auto py-6 lg:py-24 ${className}`}>
                {contentSection}
            </div>
        );
    }

    return (
        <div className={`w-full mx-auto pt-24 pb-12 ${className}`}>
            <div className="grid md:grid-cols-2 gap-16 items-center">
                {image.position !== 'right' && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <img
                            src={image.src}
                            alt={image.alt}
                            className={image.className || 'w-[250px] h-auto'}
                        />
                    </motion.div>
                )}

                <div className={image.position === 'right' ? 'md:order-first' : ''}>
                    {contentSection}
                </div>

                {image.position === 'right' && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <img
                            src={image.src}
                            alt={image.alt}
                            className={image.className || 'w-[250px] h-auto mx-auto'}
                        />
                    </motion.div>
                )}
            </div>
        </div>
    );
}

export default TitleWithDescription; 